// src/pages/modals/ProductSearchModal.js

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

/**
 * Este modal combina la funcionalidad de búsqueda de productos
 * y la selección de sugerencias (cuando un código de barras arroja varios resultados).
 *
 * Props esperadas:
 *  - open: boolean                        // Indica si el modal está abierto
 *  - onClose: () => void                 // Callback para cerrar el modal
 *
 *  // Para búsqueda de productos
 *  - productSearchTerm: string            // Término actual en el campo de búsqueda
 *  - handleProductSearch: (term) => void  // Función que ejecuta la búsqueda al tipear
 *  - onKeyDownSearch: (e) => void         // handleKeyDownProductSearch (flechas + Enter)
 *  - productResults: array de productos   // Resultados de búsqueda (filtro de borrador ya aplicado)
 *  - selectedProductIndex: number         // Índice resaltado en los resultados
 *  - handleSelectProduct: (prod) => void  // Callback al seleccionar un producto de la lista
 *  - totalProductos: number               // Cantidad de productos encontrados
 *
 *  // Para sugerencias (resultado de escaneo de código de barras)
 *  - suggestions: array de productos       // Sugerencias cuando llegan múltiples coincidencias
 *  - selectedSuggestionIndex: number       // Índice resaltado en sugerencias
 *  - handleSelectSuggestion: (prod) => void // Callback al seleccionar una sugerencia
 *  - suggestionMode: boolean               // Si true, muestra la lista de sugerencias en lugar de resultados de búsqueda
 */
const ProductSearchModal = ({
  open,
  onClose,
  // Búsqueda tradicional
  productSearchTerm,
  handleProductSearch,
  onKeyDownSearch,
  productResults = [],
  selectedProductIndex,
  handleSelectProduct,
  totalProductos = 0,
  // Sugerencias
  suggestions = [],
  selectedSuggestionIndex,
  handleSelectSuggestion,
  suggestionMode = false,
}) => {
  const formatter = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const renderProductList = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <SearchIcon sx={{ mr: 1 }} />
        <Typography variant="h6">Buscar Productos</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
     <TextField
       label="Buscar por nombre o SKU"
       type="text"
       autoComplete="off"
       value={productSearchTerm}
       onChange={(e) => handleProductSearch(e.target.value)}
       onKeyDown={onKeyDownSearch}
       fullWidth
       sx={{ mb: 1 }}
       autoFocus
     />

      <Typography variant="body2" sx={{ mb: 1 }}>
        {totalProductos} producto{totalProductos !== 1 ? 's' : ''} encontrado
        {totalProductos !== 1 ? 's' : ''}
      </Typography>

      {productResults.length === 0 ? (
        <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
          Sin resultados
        </Typography>
      ) : (
        <List>
          {productResults.map((prod, idx) => {
            const neto = Number(prod.precio_neto);
            const impuestos = prod.impuestos || [];
            const totalImpuestos = impuestos.reduce(
              (sum, t) => sum + neto * (t.porcentaje / 100),
              0
            );
            const bruto = neto + totalImpuestos;

            return (
              <ListItemButton
                key={prod.id}
                selected={selectedProductIndex === idx}
                onClick={() => handleSelectProduct(prod)}
                sx={{
                  px: 1.5,
                  py: 1,
                }}
              >
                <ListItemAvatar>
                  {prod.ruta_imagen ? (
                    <Avatar src={prod.ruta_imagen} variant="square" />
                  ) : (
                    <Avatar variant="square">
                      <LocalShippingIcon />
                    </Avatar>
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="subtitle1" noWrap>
                        {prod.nombre}{' '}
                        <Typography
                          component="span"
                          variant="caption"
                          color="textSecondary"
                        >
                          (SKU: {prod.sku}) (Cód. Barras: {prod.codigo_barras})
                        </Typography>
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        noWrap
                      >
                        {prod.descripcion}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                      <Typography variant="caption">
                        Precio: {formatter.format(bruto)}
                      </Typography>
                      <Typography variant="caption">
                        Formato de venta: {prod.stock_total} {prod.unidad}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      )}
    </>
  );

  const renderSuggestionsList = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">Sugerencias</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <List>
        {suggestions.map((prod, idx) => {
          return (
            <ListItemButton
              key={prod.id}
              selected={selectedSuggestionIndex === idx}
              onClick={() => handleSelectSuggestion(prod)}
              sx={{
                px: 1.5,
                py: 1,
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle1"
                    sx={
                      prod.estado === 'not-available'
                        ? { textDecoration: 'line-through', color: '#ef5350' }
                        : { fontWeight: 700 }
                    }
                    noWrap
                  >
                    {prod.nombre}
                  </Typography>
                }
              />
            </ListItemButton>
          );
        })}
      </List>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent sx={{ py: 2 }}>
        {suggestionMode ? renderSuggestionsList() : renderProductList()}
      </DialogContent>
    </Dialog>
  );
};

export default ProductSearchModal;
